import React, { useState, useEffect, useRef } from 'react';
import {
    View,
    ScrollView,
    TouchableOpacity,
    StyleSheet,
    Alert,
    RefreshControl,
    Modal,
    TextInput,
    Animated,
    Dimensions,
    Platform,
    ActivityIndicator
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { StatusBar } from 'expo-status-bar';
import { LinearGradient } from 'expo-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { H1, H2, H3, Body1, Body2, Caption, ButtonText } from '../../components/common/TextStyles';
import { getShadow } from '../../theme/useTheme';
import Skeleton from '../../components/common/Skeleton';
import { getWalletBalance, requestWithdrawal } from '../../api/marketplace';

import MarketplaceFooter from '../../components/common/MarketplaceFooter';

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('window');

const WalletScreen = ({ navigation }) => {
    const insets = useSafeAreaInsets();
    const scrollY = useRef(new Animated.Value(0)).current;

    const [balance, setBalance] = useState(0);
    const [savingsBalance, setSavingsBalance] = useState(0);
    const [escrowBalance, setEscrowBalance] = useState(0);

    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [withdrawModalVisible, setWithdrawModalVisible] = useState(false);
    const [withdrawAmount, setWithdrawAmount] = useState('');
    const [withdrawPhone, setWithdrawPhone] = useState('');
    const [submittingWithdrawal, setSubmittingWithdrawal] = useState(false);

    useEffect(() => {
        fetchWalletData();
    }, []);

    const fetchWalletData = async () => {
        try {
            setLoading(true);
            const data = await getWalletBalance();
            setBalance(data.balance);
            setSavingsBalance(data.savings_balance || 0);
            setEscrowBalance(data.escrow_balance || 0);

            setTransactions(data.transactions);
        } catch (error) {
            console.error('Wallet fetch error:', error);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const handleWithdraw = async () => {
        if (!withdrawAmount || isNaN(withdrawAmount) || Number(withdrawAmount) <= 0) {
            Alert.alert('Error', 'Please enter a valid amount');
            return;
        }

        try {
            setSubmittingWithdrawal(true);
            await requestWithdrawal(Number(withdrawAmount), withdrawPhone);
            setWithdrawModalVisible(false);
            setWithdrawAmount('');
            setWithdrawPhone('');
            Alert.alert('Success', 'Withdrawal request initiated successfully via M-Pesa.');
            fetchWalletData();
        } catch (error) {
            Alert.alert('Withdrawal Failed', error.toString());
        } finally {
            setSubmittingWithdrawal(false);
        }
    };

    // Header Animation values
    const headerHeight = 260;

    const headerOpacity = scrollY.interpolate({
        inputRange: [0, 80],
        outputRange: [1, 0],
        extrapolate: 'clamp',
    });

    const stickyHeaderOpacity = scrollY.interpolate({
        inputRange: [80, 120],
        outputRange: [0, 1],
        extrapolate: 'clamp',
    });

    return (
        <View style={styles.container}>
            <StatusBar style="light" />

            {/* Background Aura Section (Fixed) */}
            <View style={[styles.headerBackground, { height: headerHeight + 50 }]}>
                <LinearGradient
                    colors={['#10b981', '#059669', '#047857']}
                    style={StyleSheet.absoluteFill}
                />
                <View style={styles.auraContainer}>
                    <View style={[styles.aura, styles.aura1]} />
                    <View style={[styles.aura, styles.aura2]} />
                    <View style={[styles.aura, styles.aura3]} />
                </View>
            </View>

            {/* Mini Sticky Header */}
            <Animated.View
                style={[
                    styles.miniStickyHeader,
                    { paddingTop: insets.top + 5, opacity: stickyHeaderOpacity }
                ]}
                pointerEvents="box-none"
            >
                <LinearGradient
                    colors={['#10b981', '#059669']}
                    style={StyleSheet.absoluteFill}
                    pointerEvents="none"
                />
                <View style={styles.miniHeaderContent}>
                    <H2 style={styles.miniHeaderTitle}>Wallet</H2>
                    <TouchableOpacity onPress={() => navigation.goBack()} style={styles.miniBackBtn}>
                        <Ionicons name="chevron-back" size={20} color="#FFFFFF" />
                    </TouchableOpacity>
                </View>
            </Animated.View>

            <Animated.ScrollView
                style={styles.scrollView}
                contentContainerStyle={styles.scrollViewContent}
                showsVerticalScrollIndicator={false}
                onScroll={Animated.event(
                    [{ nativeEvent: { contentOffset: { y: scrollY } } }],
                    { useNativeDriver: true }
                )}
                scrollEventThrottle={16}
                refreshControl={
                    <RefreshControl
                        refreshing={refreshing}
                        onRefresh={fetchWalletData}
                        tintColor="#FFFFFF"
                        progressViewOffset={insets.top + 20}
                    />
                }
            >
                {/* Full Hero Header */}
                <Animated.View style={[styles.heroHeader, { paddingTop: insets.top + 20, opacity: headerOpacity }]}>
                    <View style={styles.topBar}>
                        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backCircle}>
                            <Ionicons name="arrow-back" size={22} color="#FFFFFF" />
                        </TouchableOpacity>
                        <View style={styles.headerIcons}>
                            <TouchableOpacity style={styles.iconCircle} onPress={fetchWalletData}>
                                <Ionicons name="refresh" size={20} color="#FFFFFF" />
                            </TouchableOpacity>
                        </View>
                    </View>

                    <View style={styles.heroTitleBlock}>
                        {loading ? (
                            <View style={{ gap: 8 }}>
                                <Skeleton width="60%" height={10} borderRadius={4} style={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
                                <Skeleton width="80%" height={32} borderRadius={8} style={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
                            </View>
                        ) : (
                            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                                <View>
                                    <Caption style={styles.heroSubtitle}>SPENDABLE BALANCE</Caption>
                                    <H2 style={[styles.heroTitle, { fontSize: 32 }]}>KES {Number(balance).toLocaleString()}</H2>
                                </View>
                                <TouchableOpacity
                                    style={styles.withdrawBtnHero}
                                    onPress={() => setWithdrawModalVisible(true)}
                                >
                                    <Ionicons name="arrow-up-circle" size={18} color="#10B981" />
                                    <ButtonText style={styles.withdrawBtnText}>WITHDRAW</ButtonText>
                                </TouchableOpacity>
                            </View>
                        )}
                        
                        <View style={styles.heroStatsRow}>
                            {loading ? (
                                <View style={{ flex: 1, gap: 6 }}>
                                    <Skeleton width="40%" height={8} borderRadius={4} style={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
                                    <Skeleton width="60%" height={20} borderRadius={4} style={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
                                </View>
                            ) : (
                                <>
                                    <View style={styles.heroStatItem}>
                                        <Caption style={styles.heroStatLabel}>SAVINGS & CHALLENGES</Caption>
                                        <H3 style={styles.heroStatValue}>KES {Number(savingsBalance).toLocaleString()}</H3>
                                    </View>

                                    {Number(escrowBalance) > 0 && (
                                        <View style={styles.inlineEscrow}>
                                            <View style={styles.statusDot} />
                                            <Caption style={styles.escrowLabelCompact}>KES {Number(escrowBalance).toLocaleString()} PENDING</Caption>
                                        </View>
                                    )}
                                </>
                            )}
                        </View>
                    </View>





                </Animated.View>


                {/* Content Surface */}
                <View style={styles.contentSurface}>
                    <H3 style={styles.surfaceTitle}>Recent Transactions</H3>

                    {loading ? (
                        <View style={{ gap: 10 }}>
                            <Skeleton width="100%" height={60} borderRadius={16} />
                            <Skeleton width="100%" height={60} borderRadius={16} />
                            <Skeleton width="100%" height={60} borderRadius={16} />
                            <Skeleton width="100%" height={60} borderRadius={16} />
                        </View>
                    ) : (
                        <>
                            {transactions.length === 0 && (
                                <View style={styles.emptyContainer}>
                                    <Ionicons name="receipt-outline" size={48} color="#D1D5DB" />
                                    <Body2 style={styles.emptyText}>No transactions yet.</Body2>
                                </View>
                            )}

                            {transactions.map((item) => (
                                <View key={item.id} style={styles.transactionCard}>
                                    <View style={[
                                        styles.transactionIcon,
                                        { backgroundColor: (item.type === 'deposit' || item.type === 'release_escrow') ? '#10B98115' : '#EF444415' }
                                    ]}>
                                        <Ionicons
                                            name={(item.type === 'deposit' || item.type === 'release_escrow') ? 'arrow-down' : 'arrow-up'}
                                            size={20}
                                            color={(item.type === 'deposit' || item.type === 'release_escrow') ? '#10B981' : '#EF4444'}
                                        />
                                    </View>
                                    <View style={styles.transactionDetails}>
                                        <H3 style={styles.transactionType}>{item.description || item.type.replace(/_/g, ' ').toUpperCase()}</H3>
                                        <Caption style={styles.transactionDate}>{new Date(item.createdAt).toLocaleDateString()}</Caption>
                                    </View>

                                    <H3 style={[
                                        styles.transactionAmount,
                                        { color: (item.type === 'deposit' || item.type === 'release_escrow') ? '#10B981' : '#111827' }

                            ]}>
                                {(item.type === 'deposit' || item.type === 'release_escrow') ? '+' : '-'} KES {item.amount}
                            </H3>
                        </View>
                    ))}
                        </>
                    )}

                    <View style={styles.footerSection}>
                        <Caption style={styles.footerText}>Secure Wallet • Youth Connect</Caption>
                    </View>
                </View>
            </Animated.ScrollView>
            <MarketplaceFooter activeTab="Profile" navigation={navigation} />

            {/* Withdrawal Modal */}
            <Modal
                animationType="fade"
                transparent={true}
                visible={withdrawModalVisible}
                onRequestClose={() => setWithdrawModalVisible(false)}
            >
                <View style={styles.modalOverlay}>
                    <View style={styles.modalContent}>
                        <H2 style={styles.modalTitle}>Withdraw Funds</H2>
                        <Body2 style={styles.modalDesc}>Enter the amount you wish to withdraw to your linked M-Pesa account.</Body2>

                        <TextInput
                            style={styles.input}
                            placeholder="Amount (KES)"
                            keyboardType="numeric"
                            value={withdrawAmount}
                            onChangeText={setWithdrawAmount}
                            autoFocus
                        />

                        <H3 style={[styles.modalTitle, { fontSize: 16, marginBottom: 5 }]}>M-Pesa Number</H3>
                        <TextInput
                            style={styles.input}
                            placeholder="e.g. 0712345678"
                            keyboardType="phone-pad"
                            value={withdrawPhone}
                            onChangeText={setWithdrawPhone}
                        />

                        <View style={styles.modalButtons}>
                            <TouchableOpacity
                                style={[styles.modalButton, styles.cancelButton]}
                                onPress={() => setWithdrawModalVisible(false)}
                            >
                                <Caption style={styles.cancelButtonText}>CANCEL</Caption>
                            </TouchableOpacity>
                            <TouchableOpacity
                                style={[styles.modalButton, styles.confirmButton]}
                                onPress={handleWithdraw}
                                disabled={submittingWithdrawal}
                            >
                                {submittingWithdrawal ? (
                                    <ActivityIndicator size="small" color="#FFFFFF" />
                                ) : (
                                    <Caption style={styles.confirmButtonText}>CONFIRM</Caption>
                                )}
                            </TouchableOpacity>
                        </View>
                    </View>
                </View>
            </Modal>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#ECFDF5', // Fresh Mint background
    },
    headerBackground: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 0,
    },
    auraContainer: {
        ...StyleSheet.absoluteFillObject,
        overflow: 'hidden',
    },
    aura: {
        position: 'absolute',
        borderRadius: 150,
        opacity: 0.12,
    },
    aura1: {
        width: 250,
        height: 250,
        top: -50,
        right: -30,
        backgroundColor: '#6EE7B7',
    },
    aura2: {
        width: 200,
        height: 200,
        top: 50,
        left: -50,
        backgroundColor: '#34D399',
    },
    aura3: {
        width: 180,
        height: 180,
        bottom: -60,
        right: 40,
        backgroundColor: '#064E3B',
    },
    miniStickyHeader: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        height: 90,
        elevation: 4,
    },
    miniHeaderContent: {
        flexDirection: 'row-reverse',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: 16,
        height: 44,
        marginTop: 20,
    },
    miniHeaderTitle: {
        color: '#FFFFFF',
        fontSize: 15,
        fontWeight: '800',
    },
    miniBackBtn: {
        width: 32,
        height: 32,
        borderRadius: 16,
        backgroundColor: 'rgba(255,255,255,0.2)',
        alignItems: 'center',
        justifyContent: 'center',
    },
    scrollView: {
        flex: 1,
        zIndex: 1,
    },
    scrollViewContent: {
        paddingBottom: 0,
    },
    heroHeader: {
        paddingHorizontal: 20,
        paddingBottom: 15,
        zIndex: 2,
    },

    topBar: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 15,
    },
    backCircle: {
        width: 36,
        height: 36,
        borderRadius: 18,
        backgroundColor: 'rgba(255,255,255,0.25)',
        alignItems: 'center',
        justifyContent: 'center',
    },
    iconCircle: {
        width: 36,
        height: 36,
        borderRadius: 18,
        backgroundColor: 'rgba(255,255,255,0.15)',
        alignItems: 'center',
        justifyContent: 'center',
    },
    heroTitleBlock: {
        marginBottom: 15,
    },
    heroSubtitle: {
        color: 'rgba(255,255,255,0.8)',
        letterSpacing: 1.5,
        fontWeight: '800',
        fontSize: 10,
    },
    heroTitle: {
        color: '#FFFFFF',
        fontSize: 28,
        fontWeight: '800',
        marginTop: 2,
    },
    availableLabel: {
        color: 'rgba(255,255,255,0.7)',
        marginTop: 0,
        fontSize: 11,
    },
    withdrawBtnHero: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        backgroundColor: '#FFFFFF',
        paddingHorizontal: 16,
        paddingVertical: 10,
        borderRadius: 12,
        alignSelf: 'flex-start',
        ...getShadow('sm'),
    },
    withdrawBtnText: {
        color: '#059669',
        fontSize: 11,
        fontWeight: '900',
    },
    contentSurface: {
        backgroundColor: '#ECFDF5', // Fresh Mint background
        borderTopLeftRadius: 20,
        borderTopRightRadius: 20,


        paddingTop: 24,
        paddingHorizontal: 18,
        minHeight: SCREEN_HEIGHT - 100,
        zIndex: 5,
        marginTop: -15,
    },
    escrowContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        backgroundColor: 'rgba(255,255,255,0.15)',
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 8,
        marginBottom: 15,
        alignSelf: 'flex-start',
    },
    escrowText: {
        color: '#FFFFFF',
        fontSize: 11,
    },
    escrowAmount: {
        color: '#FFFFFF',
        fontWeight: '800',
    },
    heroStatsRow: {
        marginTop: 15,
        borderTopWidth: 1,
        borderTopColor: 'rgba(255,255,255,0.15)',
        paddingTop: 15,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    heroStatItem: {
        flex: 1,
    },
    heroStatLabel: {
        color: 'rgba(255,255,255,0.6)',
        fontSize: 9,
        fontWeight: '800',
        letterSpacing: 1,
    },
    heroStatValue: {
        color: '#FFFFFF',
        fontSize: 20,
        fontWeight: '800',
        marginTop: 2,
    },
    inlineEscrow: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: 'rgba(255,255,255,0.15)',
        paddingHorizontal: 10,
        paddingVertical: 6,
        borderRadius: 20,
        gap: 6,
    },
    statusDot: {
        width: 6,
        height: 6,
        borderRadius: 3,
        backgroundColor: '#FBBF24',
    },
    escrowLabelCompact: {
        color: '#FFFFFF',
        fontSize: 10,
        fontWeight: '800',
    },
    surfaceTitle: {

        fontSize: 15,
        fontWeight: '800',
        color: '#065F46',
        marginBottom: 15,
    },
    transactionCard: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#FFFFFF',
        paddingVertical: 12,
        paddingHorizontal: 12,
        borderRadius: 16,
        marginBottom: 8,
        ...getShadow('xs'),
    },
    transactionIcon: {
        width: 36,
        height: 36,
        borderRadius: 10,
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: 12,
    },
    transactionDetails: {
        flex: 1,
    },
    transactionType: {
        fontSize: 13,
        fontWeight: '800',
        color: '#111827',
    },
    transactionDate: {
        marginTop: 1,
        fontSize: 10,
    },
    transactionAmount: {
        fontSize: 14,
        fontWeight: '800',
    },
    emptyContainer: {
        alignItems: 'center',
        paddingVertical: 40,
    },
    emptyText: {
        marginTop: 8,
        color: '#9CA3AF',
        fontSize: 12,
    },
    footerSection: {
        alignItems: 'center',
        paddingVertical: 30,
        marginTop: 10,
    },
    footerText: {
        color: '#9CA3AF',
        fontSize: 9,
    },
    modalOverlay: {
        flex: 1,
        backgroundColor: 'rgba(0,0,0,0.6)',
        justifyContent: 'center',
        padding: 20,
    },
    modalContent: {
        backgroundColor: '#FFFFFF',
        borderRadius: 20,
        padding: 20,
        ...getShadow('lg'),
    },
    modalTitle: {
        fontSize: 18,
        fontWeight: '800',
        color: '#111827',
        marginBottom: 8,
    },
    modalDesc: {
        color: '#6B7280',
        marginBottom: 20,
        lineHeight: 18,
        fontSize: 13,
    },
    input: {
        backgroundColor: '#F3F4F6',
        borderRadius: 12,
        padding: 14,
        fontSize: 16,
        fontWeight: '700',
        color: '#111827',
        marginBottom: 15,
    },
    modalButtons: {
        flexDirection: 'row',
        gap: 10,
    },
    modalButton: {
        flex: 1,
        height: 46,
        borderRadius: 12,
        alignItems: 'center',
        justifyContent: 'center',
    },
    cancelButton: {
        backgroundColor: '#F3F4F6',
    },
    confirmButton: {
        backgroundColor: '#10B981',
    },
    cancelButtonText: {
        color: '#4B5563',
        fontWeight: '900',
        fontSize: 12,
    },
    confirmButtonText: {
        color: '#FFFFFF',
        fontWeight: '900',
        fontSize: 12,
    }
});

export default WalletScreen;
